// @ts-check
// Seção "Envio de e-mails" do painel de suporte (/admin; spec/email.md seção 11.9; spec-2.0.0 K36; contrato 09 de e-mail,
// seção 12): o freio da plataforma (parar e liberar todos os envios de campanhas e fluxos), se os avisos do provedor
// estão chegando, o uso da conta por dia e, por escola, o uso de hoje e do período, o limite diário e a suspensão, com
// mudar o limite, suspender e tirar a suspensão. Só para SUPERADMIN_EMAILS (o servidor confere).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { getEmailSendingOverview, setCampaignSendingSwitch, updateEmailSendingTenant } from '../../data/email.js';
import { formatDateTime, formatNumber } from '../../lib/format.js';
import { Button } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { showOperationsError } from './operations-format.js';
import { hasTranslation, t } from '../../i18n/index.js';

const PERIOD_DAYS = 7;

/** @param {string | null | undefined} reason */
const suspendedText = (reason) => (hasTranslation(`operations.emailSending.suspendedReason.${reason}`) ? t(`operations.emailSending.suspendedReason.${reason}`) : t('operations.emailSending.suspended'));

export function EmailSendingSection() {
  const [overview, setOverview] = useState(/** @type {any} */ (null));
  const [busyTenantId, setBusyTenantId] = useState(/** @type {string | null} */ (null));
  const [isSwitching, setIsSwitching] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);
  const [limitTenant, setLimitTenant] = useState(/** @type {any} */ (null));
  const [limitText, setLimitText] = useState('');

  const load = () => getEmailSendingOverview(PERIOD_DAYS).then(setOverview).catch(showOperationsError);
  useEffect(() => {
    load();
  }, []);

  /** @param {boolean} paused */
  const toggleSwitch = async (paused) => {
    setIsSwitching(true);
    try {
      await setCampaignSendingSwitch(paused);
      setConfirmPause(false);
      load();
    } catch (error) {
      showOperationsError(error);
    } finally {
      setIsSwitching(false);
    }
  };
  /** @param {string} tenantId @param {Record<string, unknown>} patch */
  const updateTenant = async (tenantId, patch) => {
    setBusyTenantId(tenantId);
    try {
      await updateEmailSendingTenant(tenantId, /** @type {any} */ (patch));
      showSuccessSnackbar(t('operations.emailSending.updated'));
      setLimitTenant(null);
      load();
    } catch (error) {
      showOperationsError(error);
    } finally {
      setBusyTenantId(null);
    }
  };

  if (!overview) return html`<section class="admin-tenants"><h2 class="settings-section-title">${t('operations.emailSending.title')}</h2><${Skeleton} height=${120} radius=${8} /></section>`;
  const sendingSwitch = overview.sendingSwitch ?? { paused: false };
  return html`<section class="admin-tenants ec-admin" data-testid="admin-email-sending">
    <div class="admin-section-header">
      <h2 class="settings-section-title">${t('operations.emailSending.title')}</h2>
    </div>
    <p class="channel-card-description">${t('operations.emailSending.intro')}</p>

    <div class="ec-admin-switch" data-paused=${sendingSwitch.paused ? 'true' : 'false'}>
      <${Icon} name=${sendingSwitch.paused ? 'IconPlayerPause' : 'IconPlayerPlay'} size=${18} />
      <div>
        <strong>${t('operations.emailSending.switchTitle')}</strong>
        <p class="settings-table-muted">${sendingSwitch.paused
          ? t('operations.emailSending.switchPaused', { date: sendingSwitch.pausedAt ? formatDateTime(sendingSwitch.pausedAt) : '' })
          : t('operations.emailSending.switchRunning')}</p>
      </div>
      ${sendingSwitch.paused
        ? html`<${Button} title=${t('operations.emailSending.resume')} icon="IconPlayerPlay" size="small" accent="blue" isLoading=${isSwitching} onClick=${() => toggleSwitch(false)} />`
        : html`<${Button} title=${t('operations.emailSending.pause')} icon="IconPlayerPause" size="small" variant="secondary" accent="danger" onClick=${() => setConfirmPause(true)} />`}
    </div>
    <p class="settings-table-muted">${overview.eventsEnabled ? t('operations.emailSending.eventsOn') : t('operations.emailSending.eventsOff')}</p>

    <h3 class="ec-section-title">${t('operations.emailSending.accountTitle')}</h3>
    <ul class="admin-list">
      ${(overview.account ?? []).map((/** @type {any} */ day) => html`<li key=${day.day}><span>${t('operations.emailSending.accountDay', {
        day: day.day, sent: formatNumber(day.sent), limit: formatNumber(day.limit),
      })}</span></li>`)}
    </ul>

    <h3 class="ec-section-title">${t('operations.emailSending.tenantsTitle')}</h3>
    ${(overview.tenants ?? []).length === 0
      ? html`<p class="channel-card-description">${t('operations.emailSending.empty')}</p>`
      : html`<div class="settings-table">
          <div class="settings-table-row settings-table-header ec-admin-row">
            <span>${t('operations.emailSending.columns.school')}</span><span class="settings-table-number">${t('operations.emailSending.columns.today')}</span>
            <span class="settings-table-number">${t('operations.emailSending.columns.period', { days: PERIOD_DAYS })}</span>
            <span class="settings-table-number">${t('operations.emailSending.columns.limit')}</span><span>${t('operations.emailSending.columns.state')}</span>
            <span>${t('operations.emailSending.columns.actions')}</span>
          </div>
          ${overview.tenants.map((/** @type {any} */ tenant) => html`<div key=${tenant.tenantId} class="settings-table-row ec-admin-row">
            <span class="settings-table-name"><span>${tenant.displayName}</span><span class="settings-table-muted">${tenant.slug}</span></span>
            <span class="settings-table-number">${formatNumber(tenant.todaySent)}</span>
            <span class="settings-table-number">${formatNumber(tenant.periodSent)}</span>
            <span class="settings-table-number">${formatNumber(tenant.dailyLimit)} <span class="settings-table-muted">(${t(tenant.hasCustomLimit ? 'operations.emailSending.customLimit' : 'operations.emailSending.defaultLimit')})</span></span>
            <span>${tenant.suspended
              ? html`<span class="ec-badge" data-tone="red">${suspendedText(tenant.suspended.reason)}</span>`
              : html`<span class="ec-badge" data-tone="green">${t('operations.emailSending.active')}</span>`}</span>
            <span class="ec-button-row">
              <${Button} title=${t('operations.emailSending.setLimit')} size="small" variant="secondary" onClick=${() => {
                setLimitTenant(tenant);
                setLimitText(tenant.hasCustomLimit ? String(tenant.dailyLimit) : '');
              }} />
              ${tenant.suspended
                ? html`<${Button} title=${t('operations.emailSending.liftSuspension')} size="small" variant="secondary" isLoading=${busyTenantId === tenant.tenantId}
                    onClick=${() => updateTenant(tenant.tenantId, { liftSuspension: true })} />`
                : html`<${Button} title=${t('operations.emailSending.suspend')} size="small" variant="secondary" accent="danger" isLoading=${busyTenantId === tenant.tenantId}
                    onClick=${() => updateTenant(tenant.tenantId, { suspend: { reason: 'MANUAL' } })} />`}
            </span>
          </div>`)}
        </div>`}

    ${confirmPause
      ? html`<${Modal} title=${t('operations.emailSending.pause')} subtitle=${t('operations.emailSending.pauseConfirm')} confirmLabel=${t('operations.emailSending.pause')}
          confirmAccent="danger" isLoading=${isSwitching} onClose=${() => setConfirmPause(false)} onConfirm=${() => toggleSwitch(true)} />`
      : null}
    ${limitTenant
      ? html`<${Modal} title=${t('operations.emailSending.setLimit')} subtitle=${limitTenant.displayName} confirmLabel=${t('common.save')}
          isLoading=${busyTenantId === limitTenant.tenantId} onClose=${() => setLimitTenant(null)}
          onConfirm=${() => updateTenant(limitTenant.tenantId, { dailyLimit: limitText.trim() === '' ? null : Math.round(Number(limitText)) })}>
          <label class="ec-field">
            <span class="ec-field-label">${t('operations.emailSending.limitPrompt')}</span>
            <input class="text-input" type="number" min="1" value=${limitText} onInput=${(/** @type {Event} */ event) => setLimitText(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          </label>
        <//>`
      : null}
  </section>`;
}
